import { Injectable } from '@angular/core';

const CREDENTIAL_KEY = 'wp_biometric_credential_id';
const REGISTRATION_OFFERED_KEY = 'wp_biometric_registration_offered';
const RP_NAME = 'Wine Passport';

@Injectable({ providedIn: 'root' })
export class BiometricAuthService {

  /** Verifica si WebAuthn está disponible en el navegador */
  isSupported(): boolean {
    return !!(
      window.isSecureContext &&
      navigator.credentials &&
      (navigator.credentials as any).create &&
      (navigator.credentials as any).get
    );
  }

  /** Verifica si ya hay una credencial biométrica registrada */
  hasCredential(): boolean {
    return !!localStorage.getItem(CREDENTIAL_KEY);
  }

  /** Verifica si ya se ofreció el registro (para no re-preguntar en cada login) */
  wasRegistrationOffered(): boolean {
    return localStorage.getItem(REGISTRATION_OFFERED_KEY) === 'true';
  }

  /** Indica si se debe mostrar el prompt de registro */
  shouldPromptRegistration(): boolean {
    return this.isSupported() && !this.hasCredential() && !this.wasRegistrationOffered();
  }

  /** Registra una nueva credencial biométrica (Face ID / huella) */
  async register(): Promise<boolean> {
    if (!this.isSupported()) return false;

    // Marcar que ya se ofreció el registro (para no re-preguntar)
    localStorage.setItem(REGISTRATION_OFFERED_KEY, 'true');

    try {
      const challenge = this.randomBytes(32);
      const userId = this.randomBytes(16);

      const publicKey: PublicKeyCredentialCreationOptions = {
        challenge,
        rp: { name: RP_NAME },
        user: {
          id: userId,
          name: 'winepassport-user',
          displayName: 'Wine Passport',
        },
        pubKeyCredParams: [
          { alg: -7, type: 'public-key' },   // ES256
          { alg: -257, type: 'public-key' },  // RS256
        ],
        authenticatorSelection: {
          authenticatorAttachment: 'platform',
          userVerification: 'required',
        },
        timeout: 60000,
        attestation: 'none',
      };

      const credential = await (navigator.credentials as any).create({ publicKey }) as PublicKeyCredential | null;
      if (credential) {
        const credId = this.bufferToBase64Url(credential.rawId);
        localStorage.setItem(CREDENTIAL_KEY, credId);
        console.log('Biometría registrada exitosamente');
        return true;
      }
      return false;
    } catch (err: any) {
      // NotAllowedError = usuario canceló, NotSupportedError = sin authenticator platform
      console.warn('No se pudo registrar biometría:', err?.message || err);
      return false;
    }
  }

  /** Autentica al usuario con biometría usando la credencial registrada */
  async authenticate(): Promise<boolean> {
    if (!this.isSupported()) return false;

    const credIdBase64 = localStorage.getItem(CREDENTIAL_KEY);
    if (!credIdBase64) return false;

    try {
      const allowCredentials: PublicKeyCredentialDescriptor[] = [
        {
          id: this.base64UrlToBuffer(credIdBase64),
          type: 'public-key',
        },
      ];

      const publicKey: PublicKeyCredentialRequestOptions = {
        challenge: this.randomBytes(32),
        allowCredentials,
        userVerification: 'required',
        timeout: 60000,
      };

      const assertion = await (navigator.credentials as any).get({ publicKey }) as PublicKeyCredential | null;
      return !!assertion;
    } catch (err: any) {
      console.warn('Autenticación biométrica fallida:', err?.message || err);
      return false;
    }
  }

  /** Elimina la credencial almacenada y resetea el flag de registro (útil al cerrar sesión) */
  clearCredential(): void {
    localStorage.removeItem(CREDENTIAL_KEY);
    localStorage.removeItem(REGISTRATION_OFFERED_KEY);
  }

  // ─── Helpers ──────────────────────────────────────────

  private randomBytes(length: number): Uint8Array {
    const bytes = new Uint8Array(length);
    crypto.getRandomValues(bytes);
    return bytes;
  }

  private bufferToBase64Url(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  private base64UrlToBuffer(base64url: string): Uint8Array {
    const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
    const pad = base64.length % 4;
    const padded = pad ? base64 + '='.repeat(4 - pad) : base64;
    const binary = atob(padded);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  }
}
